
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubjects } from '@/hooks/useSubjects';
import { useTutors } from '@/hooks/useTutors';
import { Book, Dumbbell, Code, MapPin } from 'lucide-react';

interface SubjectSpecificFiltersProps {
  selectedSubject: string | null;
  onSubjectChange: (subject: string | null) => void;
}

const SubjectSpecificFilters = ({ selectedSubject, onSubjectChange }: SubjectSpecificFiltersProps) => {
  const { subjects } = useSubjects();
  const { searchTutors } = useTutors();
  const [tutorCounts, setTutorCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Filter out invalid subjects and get only focus subjects
  const focusSubjects = subjects.filter(subject => 
    subject.category === 'focus' && 
    subject.name && 
    subject.name.trim() !== '' && 
    subject.name !== '0' && 
    subject.name.length > 0
  );


  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Book': return Book;
      case 'Dumbbell': return Dumbbell;
      case 'Code': return Code;
      case 'MapPin': return MapPin;
      default: return Book;
    }
  };

  // Fetch tutor counts for focus subjects
  useEffect(() => {
    const fetchTutorCounts = async () => {
      if (focusSubjects.length === 0) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      
      
      const counts: Record<string, number> = {};
      
      // Count tutors for each valid focus subject
      for (const subject of focusSubjects) {
        try {
          const results = await searchTutors({
            searchQuery: '',
            selectedSubject: subject.name,
            selectedCountry: '',
            priceRange: '',
            availability: ''
          });
          counts[subject.name] = results.length;
          
        } catch (error) {
          
          counts[subject.name] = 0;
        }
      }
      
      setTutorCounts(counts);
      setIsLoading(false);
    };

    fetchTutorCounts();
  }, [focusSubjects.length]); // Only depend on length to avoid infinite loops

  // Don't render anything if no valid subjects
  if (focusSubjects.length === 0) {
    
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
      <h3 className="text-lg font-semibold mb-4">Focus Subjects</h3>
      <div className="flex flex-wrap gap-3">
        <Button
          variant={selectedSubject === null ? "default" : "outline"}
          size="sm"
          onClick={() => onSubjectChange(null)}
        >
          All Subjects
        </Button>
        
        {/* Only render valid focus subjects */}
        {focusSubjects.map((subject) => {
          const IconComponent = getIconComponent(subject.icon || 'Book');
          const tutorCount = tutorCounts[subject.name] || 0;
          
          return (
            <Button
              key={subject.id}
              variant={selectedSubject === subject.name ? "default" : "outline"}
              size="sm"
              onClick={() => onSubjectChange(subject.name)}
              className="flex items-center gap-2"
            >
              <IconComponent className="h-4 w-4" />
              {subject.name}
              {!isLoading && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {tutorCount}
                </Badge>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default SubjectSpecificFilters;
